import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Users, Trophy, Target, Brain, Zap, Shield, Activity, Building2, Mail, Phone, Calendar } from 'lucide-react';

const Investors: React.FC = () => {
  const metrics = [
    {
      icon: <TrendingUp className="w-8 h-8" />,
      value: "$1.5M",
      label: "Current Valuation",
      color: "text-cresento-accent"
    },
    {
      icon: <Trophy className="w-8 h-8" />,
      value: "14",
      label: "Trophies Won",
      color: "text-cresento-secondary"
    },
    {
      icon: <Users className="w-8 h-8" />,
      value: "50+",
      label: "Performance Metrics",
      color: "text-cresento-accent"
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      value: "3",
      label: "Elite Club Partnerships",
      color: "text-cresento-secondary"
    }
  ];

  const achievements = [
    {
      icon: <Brain className="w-6 h-6" />,
      title: 'AI-Enabled Prototypes',
      description: 'Successfully developed fully integrated AI-enabled shin pad prototypes'
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Seamless Connectivity',
      description: 'Established robust Bluetooth (BLE) and Wi-Fi data transfer protocols'
    },
    {
      icon: <Activity className="w-6 h-6" />,
      title: 'Intuitive Mobile App',
      description: 'Created sophisticated companion mobile app with exceptional UX'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Positive Validation',
      description: 'Received overwhelmingly positive user validation from initial testing'
    },
    {
      icon: <Trophy className="w-6 h-6" />,
      title: 'Market Positioning',
      description: 'Positioned as innovative competitor to Playermaker, Soccerment, and Gengee'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Professional Recognition',
      description: 'Trusted by Hong Kong Premier League players and elite athletes'
    }
  ];

  const partnerships = [
    {
      type: "Elite Clubs",
      partners: ["Inter Milan", "Juventus"],
      description: "Strategic partnerships with top-tier football clubs for product validation and market penetration"
    },
    {
      type: "Academic Institutions",
      partners: ["HKUST Dream Builder Program"],
      description: "Research and development partnerships with leading universities for technological advancement"
    },
    {
      type: "Professional Leagues",
      partners: ["Hong Kong Premier League"],
      description: "Professional league endorsements and real-world testing with elite athletes"
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-cresento-primary to-cresento-dark text-white py-24 pt-32">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto text-center"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-cresento-light to-cresento-accent bg-clip-text text-transparent">
              Do you want to be a part of the future of sportstech?
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Join us in revolutionizing sports analytics. Explore partnership, sponsorship, and investment opportunities with Cresento.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Metrics Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-cresento-dark/40 to-black">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent mb-4">
              Investment Highlights
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Strong fundamentals and proven market traction position Cresento for explosive growth
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {metrics.map((metric, index) => (
              <motion.div
                key={metric.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 border border-cresento-secondary/30 hover:border-cresento-secondary/60 transition-all duration-300 text-center"
              >
                <div className={`${metric.color} mb-4 flex justify-center`}>
                  {metric.icon}
                </div>
                <div className={`text-4xl font-bold mb-2 ${metric.color}`}>
                  {metric.value}
                </div>
                <div className="text-gray-300 font-medium">{metric.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones Achieved */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-black to-cresento-primary/20">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">
              Proven Track Record
            </h2>
          </motion.div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {achievements.map((achievement, index) => (
                <motion.div
                  key={achievement.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 border border-cresento-secondary/30 hover:border-cresento-secondary/60 transition-all duration-300 hover:scale-105"
                >
                  <div className="flex items-center mb-4">
                    <div className="text-cresento-accent mr-3">
                      {achievement.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-white">{achievement.title}</h3>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    {achievement.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Partnerships */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-cresento-primary/20 to-cresento-dark/30">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">
              Strategic Partnerships
            </h2>
            <p className="text-xl text-gray-300 mt-4 max-w-2xl mx-auto">
              Established relationships with industry leaders validate our technology and market approach
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-8">
            {partnerships.map((partnership, index) => (
              <motion.div
                key={partnership.type}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 sm:p-8 border border-cresento-secondary/30"
              >
                <h3 className="text-2xl font-bold text-cresento-accent mb-3">{partnership.type}</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {partnership.partners.map((partner) => (
                    <span 
                      key={partner}
                      className="bg-gradient-to-r from-cresento-secondary to-cresento-accent text-white px-3 py-1 rounded-full text-sm font-semibold"
                    >
                      {partner}
                    </span>
                  ))}
                </div>
                <p className="text-gray-300 leading-relaxed">{partnership.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Opportunity */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-cresento-dark/30 to-black">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent mb-6">
                Investment Opportunity
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Join us in transforming the $5.57B global sports analytics market
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-cresento-primary/20 to-cresento-dark/30 rounded-2xl p-6 border border-cresento-secondary/30">
                <h3 className="text-2xl font-bold text-cresento-secondary mb-4">Market Opportunity</h3>
                <ul className="space-y-3 text-gray-300">
                  <li>• $5.57B global sports analytics market</li>
                  <li>• 22.1% CAGR through 2028</li>
                  <li>• Underserved amateur/youth segment</li>
                  <li>• First-mover advantage in shin pad analytics</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-cresento-accent/20 to-cresento-dark/30 rounded-2xl p-6 border border-cresento-accent/30">
                <h3 className="text-2xl font-bold text-cresento-accent mb-4">Competitive Advantage</h3>
                <ul className="space-y-3 text-gray-300">
                  <li>• Seamless integration into existing equipment</li>
                  <li>• AI-powered actionable insights</li>
                  <li>• Proven validation from elite clubs</li>
                  <li>• Affordable democratized solution</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-r from-cresento-secondary to-cresento-accent">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Ready to Partner With Us?
            </h2>
            <p className="text-xl mb-8 text-gray-200">
              Explore partnership, sponsorship, and investment opportunities. Let's discuss how we can achieve success together.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
              <motion.a
                href="mailto:partnerships@cresento.com"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-cresento-secondary hover:bg-gray-100 font-semibold py-4 px-6 rounded-lg transition-all duration-300 inline-flex items-center justify-center"
              >
                <Mail className="mr-2 h-5 w-5" />
                Email Us
              </motion.a>

              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-cresento-secondary hover:bg-gray-100 font-semibold py-4 px-6 rounded-lg transition-all duration-300 inline-flex items-center justify-center"
              >
                <Phone className="mr-2 h-5 w-5" />
                Contact Form
              </motion.a>

              <motion.a
                href="https://calendly.com/cresento"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-cresento-secondary hover:bg-gray-100 font-semibold py-4 px-6 rounded-lg transition-all duration-300 inline-flex items-center justify-center"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Call
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Investors;